import { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Search, Filter, ArrowUpDown } from "lucide-react";
import { DataTable, DepartmentBadge } from "@/components/UIComponents";
import { formatCurrency, MONTHS, CONFIG } from "@/lib/config";
import api from "@/lib/api";

export const Transactions = () => {
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedYear, setSelectedYear] = useState("2025");
  const [selectedMonth, setSelectedMonth] = useState("all");
  const [selectedDept, setSelectedDept] = useState("all");
  const [selectedType, setSelectedType] = useState("all");

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const params = {
          limit: 500
        };
        if (selectedYear !== "all") params.year = selectedYear;
        if (selectedMonth !== "all") params.month = selectedMonth;
        if (selectedDept !== "all") params.departement = selectedDept;
        if (selectedType !== "all") params.type_transaction = selectedType;
        
        const data = await api.getTransactions(params);
        setTransactions(data);
      } catch (error) {
        console.error("Error:", error);
      }
      setLoading(false);
    };
    fetchData();
  }, [selectedYear, selectedMonth, selectedDept, selectedType]);

  const filteredTransactions = useMemo(() => {
    if (!searchTerm) return transactions;
    return transactions.filter(t => 
      t.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.categorie?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [transactions, searchTerm]);

  const totals = useMemo(() => {
    const revenus = filteredTransactions.filter(t => t.type_transaction === "revenu").reduce((sum, t) => sum + t.montant, 0);
    const depenses = filteredTransactions.filter(t => t.type_transaction === "depense").reduce((sum, t) => sum + t.montant, 0);
    return { revenus, depenses, solde: revenus - depenses };
  }, [filteredTransactions]);

  const columns = [
    { key: "date", header: "Date", className: "money-value text-sm text-slate-600" },
    { key: "type_operation", header: "Opération", className: "text-sm text-slate-500" },
    { key: "description", header: "Description", className: "text-sm text-slate-900" },
    { 
      key: "categorie", 
      header: "Catégorie",
      render: (val) => val ? <Badge variant="outline" className="text-xs">{val}</Badge> : "-"
    },
    { 
      key: "departement", 
      header: "Département",
      render: (val) => <DepartmentBadge dept={val} />
    },
    { 
      key: "type_transaction", 
      header: "Type",
      render: (val) => (
        <span className={`text-xs px-2 py-1 rounded-full font-medium ${val === 'revenu' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
          {val === 'revenu' ? 'Revenu' : 'Dépense'}
        </span>
      )
    },
    { 
      key: "montant", 
      header: "Montant", 
      align: "right",
      render: (val, row) => (
        <span className={`money-value font-medium ${row.type_transaction === 'revenu' ? 'text-emerald-600' : 'text-red-500'}`}>
          {row.type_transaction === 'revenu' ? '+' : '-'}{formatCurrency(val)}
        </span>
      )
    }
  ];

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-900"></div></div>;
  }

  return (
    <div data-testid="transactions-page" className="space-y-6">
      {/* Header */}
      <div className="animate-fade-in">
        <h1 className="font-heading text-3xl font-bold text-slate-900">Transactions</h1>
        <p className="text-slate-500 mt-1">Toutes les transactions bancaires</p>
      </div>

      {/* Totaux */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 animate-fade-in delay-100">
        <Card>
          <CardContent className="p-5">
            <p className="kpi-label text-slate-500">Total Revenus</p>
            <p className="kpi-value text-2xl text-emerald-600 mt-1">{formatCurrency(totals.revenus)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <p className="kpi-label text-slate-500">Total Dépenses</p>
            <p className="kpi-value text-2xl text-red-500 mt-1">{formatCurrency(totals.depenses)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <p className="kpi-label text-slate-500">Solde Net</p>
            <p className={`kpi-value text-2xl mt-1 ${totals.solde >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
              {formatCurrency(totals.solde)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filtres */}
      <Card className="animate-fade-in delay-200">
        <CardContent className="p-5">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-64">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  data-testid="search-input"
                  placeholder="Rechercher..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={selectedYear} onValueChange={setSelectedYear}>
              <SelectTrigger className="w-28">
                <SelectValue placeholder="Année" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes</SelectItem>
                <SelectItem value="2025">2025</SelectItem>
                <SelectItem value="2024">2024</SelectItem>
              </SelectContent>
            </Select>
            <Select value={selectedMonth} onValueChange={setSelectedMonth}>
              <SelectTrigger className="w-36">
                <SelectValue placeholder="Mois" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                {MONTHS.slice(1).map((m, idx) => (
                  <SelectItem key={m.id || m._id || idx} value={String(idx + 1)}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={selectedDept} onValueChange={setSelectedDept}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Département" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                {CONFIG.departments.map(d => (
                  <SelectItem key={d} value={d}>{CONFIG.department_names[d]}</SelectItem>
                ))}
                <SelectItem value="GLOBAL">Global</SelectItem>
              </SelectContent>
            </Select>
            <Select value={selectedType} onValueChange={setSelectedType}>
              <SelectTrigger className="w-32">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                <SelectItem value="revenu">Revenus</SelectItem>
                <SelectItem value="depense">Dépenses</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Tableau */}
      <Card className="animate-fade-in delay-300">
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <Filter className="w-5 h-5" />
            {filteredTransactions.length} transactions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <DataTable columns={columns} data={filteredTransactions.slice(0, 100)} />
          {filteredTransactions.length > 100 && (
            <p className="text-center text-sm text-slate-500 mt-4">
              Affichage de 100 sur {filteredTransactions.length} transactions
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Transactions;
